import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CollectionManageAccessFacade } from '@mode/collections/domain';
import { SpaceDomain } from '@mode/shared/contract-common';
import { combineLatest, Observable, Subscription } from 'rxjs';

import { AccessItemType, AccessMenuItem } from '../access-selector/access-selector.component';
import { ManageAccessAlertMessage, ManageAccessAlertType } from '@mode/shared/ui';
import { trackByFactory } from '@mode/capra';
import { animate, style, transition, trigger } from '@angular/animations';
import { CollectionAccessLevel, SpaceType } from '@mode/shared/contract-common';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { first, last, map } from 'rxjs/operators';
import { AnalyticsService } from '@mode/shared/util-segment';

@Component({
  selector: 'mode-manage-access-dialog',
  templateUrl: './manage-access-dialog.component.html',
  styleUrls: ['./manage-access-dialog.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [style({ opacity: 1 }), animate('300ms 2s ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageAccessDialogComponent implements OnChanges, OnDestroy, OnInit {
  canBulkRemove$: Observable<boolean>;
  defaultAccessLevel$: Observable<CollectionAccessLevel>;
  explicitEntitlements$: Observable<Array<SpaceDomain.Entitlement>>;
  explicitEntitlements: SpaceDomain.Entitlement[];
  loadingEntitlements$: Observable<boolean>;

  defaultAccessSub: Subscription;
  explicitEntitlementSub: Subscription;

  selectorType: AccessItemType;
  showPersonalContent = false;
  showEmptyAccess = false;

  modeAlert: ManageAccessAlertMessage;

  // used to track if any @fadeOut animations are in flight
  animationCounter = 0;

  @Input()
  collectionId: string;

  @Input()
  collectionType: SpaceType;

  @Input()
  overallAccess: CollectionAccessLevel = undefined;
  // todo: remove overallAccess
  //  If home routes and manage access used the same service/cache for the collection
  //  this binding wouldn't be necessary

  @Input()
  workspaceName: string;

  @Input()
  showAlert = false;

  @Output()
  done = new EventEmitter<void>();

  getEntitlementToken = trackByFactory<SpaceDomain.Entitlement>(({ token }) => token);

  constructor(
    private analyticsService: AnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private collectionManageAccessFacade: CollectionManageAccessFacade,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.canBulkRemove$ = this.collectionManageAccessFacade.canDestroyEntitlements$;

    this.explicitEntitlements$ = this.collectionManageAccessFacade.explicitEntitlements$;

    this.loadingEntitlements$ = this.collectionManageAccessFacade.loadingEntitlements$.asObservable();
  }

  ngOnInit() {
    this.explicitEntitlementSub = this.explicitEntitlements$.subscribe((entitlements) => {
      this.explicitEntitlements = entitlements;
      // hack to make sure that the updateShowEmptyAccess happens
      // one tick after the explicitEntitlements have been set, so that animations lifecycles start
      setTimeout(() => {
        this.updateShowEmptyAccess(!entitlements?.length);
      }, 0);
    });

    this.defaultAccessSub = this.collectionManageAccessFacade.defaultAccessLevel$.subscribe(
      (val: CollectionAccessLevel) => {
        this.overallAccess = val;
      }
    );

    this.collectionManageAccessFacade.loadSpace(this.workspaceName, this.collectionId);

    this.setPersonalContent(this.collectionType);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.showAlert && changes.showAlert.currentValue) {
      this.addAlert(this.showAlert);
    }
  }

  ngOnDestroy() {
    this.collectionManageAccessFacade.clearUpdateSuccessAndErrors();
    this.collectionManageAccessFacade.clearEntitlements();

    this.defaultAccessSub.unsubscribe();
    this.explicitEntitlementSub.unsubscribe();
  }

  addAlert(membersAdded: boolean) {
    if (membersAdded) {
      this.setAlert({ text: 'Members added', type: ManageAccessAlertType.Success });
    } else {
      this.clearAlert();
    }
  }

  setAlert(message: ManageAccessAlertMessage) {
    this.modeAlert = message;
  }

  clearAlert() {
    this.modeAlert = null;
  }

  private warningAlert() {
    this.setAlert({
      text: 'Removing all additional access may change Member permissions, and is not reversible.',
      type: ManageAccessAlertType.Warn,
      confirm: true,
    });
  }

  handleDone() {
    this.done.emit();
  }

  handleAddMembersClick() {
    this.analyticsService.run('track', 'UI Clicked', {
      location: 'Manage collection access',
      resource: 'collection',
      token: this.collectionId,
      action: 'Add Members',
    });
    this.handleDone();
  }

  handleRemoveAllClick() {
    this.warningAlert();
  }

  handleAlertRemoveAllClick() {
    this.collectionManageAccessFacade.deleteAllEntitlements(this.workspaceName, this.collectionId);
    this.clearAlert();
  }

  handleAlertCancelClick() {
    this.clearAlert();
  }

  handleAlertBackgroundClick() {
    this.clearAlert();
  }

  updateShowEmptyAccess(showEmptyAccess: boolean) {
    this.showEmptyAccess = showEmptyAccess && this.animationCounter === 0;
    this.changeDetectorRef.markForCheck();
  }

  handleAccessorFadeOutStart(): void {
    this.animationCounter = this.animationCounter + 1;
  }

  handleAccessorFadeOutEnd(): void {
    this.animationCounter = this.animationCounter - 1;
    const hasEntitlements = !!this.explicitEntitlements.length;
    this.updateShowEmptyAccess(!hasEntitlements);
  }

  defaultAccessLevelChange(menuItem: Partial<AccessMenuItem>) {
    const currentAccessLevel = this.overallAccess;
    const newAccessLevel = menuItem.id;

    if (newAccessLevel && newAccessLevel !== currentAccessLevel) {
      this.collectionManageAccessFacade.updateDefaultAccessLevel(
        this.workspaceName,
        this.collectionId,
        newAccessLevel as CollectionAccessLevel
      );

      this.trackAccessChange(this.collectionId, newAccessLevel);
    }
  }

  trackAccessChange(token: string, menu_selection: string) {
    this.analyticsService.run('track', 'Menu Selected', {
      location: 'Manage collection access',
      resource: 'collection',
      token,
      action: 'Set default access',
      menu_selection,
    });
  }

  trackEntitlementChange(token: string, menu_selection: string, accessor_id: string, accessor_type: string) {
    this.analyticsService.run('track', 'Menu Selected', {
      location: 'Manage collection access',
      resource: 'collection',
      token,
      menu_selection,
      accessor_type,
      accessor_id,
    });
  }

  entitlementChange(menuItem: Partial<AccessMenuItem>, entitlement: SpaceDomain.Entitlement): void {
    const facade = this.collectionManageAccessFacade;

    if (menuItem.id === 'remove' && entitlement.token) {
      this.trackEntitlementChange(
        this.collectionId,
        menuItem.id,
        entitlement.accessor.token,
        entitlement.accessor.type
      );

      return facade.deleteEntitlement(this.workspaceName, this.collectionId, entitlement.token);
    }

    if (menuItem.id !== entitlement.action) {
      this.trackEntitlementChange(
        this.collectionId,
        menuItem.id,
        entitlement.accessor.token,
        entitlement.accessor.type
      );

      return facade.updateEntitlement(this.workspaceName, this.collectionId, entitlement.token, menuItem.id);
    }
  }

  displayBackground() {
    return !!this.modeAlert?.confirm;
  }

  setPersonalContent(collectionType: string) {
    this.showPersonalContent = collectionType === SpaceType.private;
  }
}
